import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Typography } from '@material-tailwind/react';
import { PlusCircleIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

export const TabRodados = ({ id_obrero, esVisualizacion }) => {
    const [rodados, setRodados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rangos, setRangos] = useState([]);
    const [selectedRodado, setSelectedRodado] = useState(null);
    
    const initialFormState = { 
        id_rodado: null, id_rango: '', dominio: '', marca: '', modelo: '', 
        f_incr: '', lugar_inscr: '', vigente: true, vendido: false, 
        transferido: false, titulo: false, exencion: false, poliza: false, 
        seguro: false, observaciones: '' 
    };
    const [form, setForm] = useState(initialFormState);
    // Guardamos el rodado tal cual lo devolvió el backend para preservar, al guardar,
    // cualquier campo que no editamos en el formulario (evita perder datos como id_rango).
    const [rodadoOriginal, setRodadoOriginal] = useState({});

    useEffect(() => {
        if(id_obrero) {
            buscarRodados(id_obrero);
            traerRangos();
        }
    }, [id_obrero]);

    useEffect(() => {
        if (selectedRodado && selectedRodado.id_rodado !== form.id_rodado) {
            abrirEditar(selectedRodado.id_rodado);
        }
    }, [selectedRodado]);

    const traerRangos = async () => {
        try {
            const response = await Axios.get(`/api/tablasgenerales/listar-rangos`);
            setRangos(response.data);
        } catch (err) {}
    };

    const buscarRodados = async (id) => {
        setLoading(true);
        try {
            const { data } = await Axios.get(`/api/tablasgenerales/rodados/${id}`);
            setRodados(data);
            if (data && data.length > 0 && !selectedRodado) {
                // setSelectedRodado dispara el useEffect que llama a abrirEditar (evitamos el doble fetch)
                setSelectedRodado(data[0]);
            } else if (data.length === 0) {
                setForm(initialFormState);
                setRodadoOriginal({});
                setSelectedRodado(initialFormState);
            }
        } catch (err) {
            console.log('Error', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const formatearFechaParaInput = (fechaText) => {
        if (!fechaText) return '';
        if (fechaText.includes('-')) return fechaText.substring(0, 10);
        const partes = fechaText.split('/');
        if (partes.length === 3) return `${partes[2]}-${partes[1]}-${partes[0]}`;
        return fechaText;
    };

    const abrirNuevo = () => {
        setForm(initialFormState);
        setRodadoOriginal({});
        setSelectedRodado(initialFormState);
    };

    const abrirEditar = async (id) => {
        if(!id) return;
        try {
            const { data } = await Axios.get(`/api/tablasgenerales/rodado/${id}`);
            if(data && data.length > 0) {
                const rodadoEdit = data[0];
                setRodadoOriginal(rodadoEdit);
                setForm({
                    id_rodado: rodadoEdit.id_rodado,
                    id_rango: rodadoEdit.id_rango || '',
                    dominio: rodadoEdit.dominio || '',
                    marca: rodadoEdit.marca || '',
                    modelo: rodadoEdit.modelo || '',
                    f_incr: formatearFechaParaInput(rodadoEdit.f_incr || rodadoEdit.fecha_inscripcion),
                    lugar_inscr: rodadoEdit.lugar_inscr || '',
                    vigente: !!rodadoEdit.vigente,
                    vendido: !!rodadoEdit.vendido,
                    transferido: !!rodadoEdit.transferido,
                    titulo: !!rodadoEdit.titulo,
                    exencion: !!rodadoEdit.exencion,
                    poliza: !!rodadoEdit.poliza,
                    seguro: !!rodadoEdit.seguro,
                    observaciones: rodadoEdit.observaciones || ''
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const borrarRodado = (id, e) => {
        if (e) e.stopPropagation();
        if(esVisualizacion) return;
        Swal.fire({
            title: '¿Eliminar Rodado?',
            text: "Esta acción no se puede deshacer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await Axios.delete(`/api/tablasgenerales/rodado/${id}`);
                    Swal.fire('Eliminado!', 'El rodado fue eliminado.', 'success');
                    setSelectedRodado(null);
                    setForm(initialFormState);
                    buscarRodados(id_obrero);
                } catch (error) {
                    Swal.fire('Error', 'Ocurrió un error.', 'error');
                }
            }
        });
    };

    const guardarRodado = async () => {
        let payload = {
            // Preservamos lo que devolvió el backend para no perder campos que no editamos
            ...rodadoOriginal,
            // y superponemos los valores del formulario (las ediciones siempre ganan)
            id_rodado: form.id_rodado,
            id_obrero: id_obrero,
            id_rango: form.id_rango ? Number(form.id_rango) : (rodadoOriginal.id_rango ?? null),
            dominio: form.dominio,
            marca: form.marca,
            modelo: form.modelo,
            f_incr: form.f_incr || null,
            lugar_inscr: form.lugar_inscr,
            vigente: form.vigente ? 1 : 0,
            vendido: form.vendido ? 1 : 0,
            transferido: form.transferido ? 1 : 0,
            titulo: form.titulo ? 1 : 0,
            exencion: form.exencion ? 1 : 0,
            poliza: form.poliza ? 1 : 0,
            seguro: form.seguro ? 1 : 0,
            observaciones: form.observaciones
        };

        try {
            if (form.id_rodado) {
                await Axios.put('/api/tablasgenerales/rodado', payload);
                Swal.fire('Actualizado', 'Rodado actualizado', 'success');
            } else {
                await Axios.post('/api/tablasgenerales/rodado', payload);
                Swal.fire('Guardado', 'Rodado creado', 'success');
            }
            buscarRodados(id_obrero);
        } catch (error) {
            const detalle = error?.response?.data?.message || error?.response?.data || error.message;
            Swal.fire('Error', `Ocurrió un error al guardar el rodado.${detalle ? ' ' + detalle : ''}`, 'error');
        }
    };

    return (
        <div className="bg-white p-5 rounded-md text-sm w-full">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full">
                {/* Left side: List box */}
                <div className="flex flex-col col-span-1 lg:col-span-5 border border-gray-300 bg-white shadow-sm h-full max-h-[500px] overflow-hidden">
                    <div className="bg-gray-100 border-b border-gray-300 flex justify-between items-center px-4 py-3 shrink-0">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Rodados</span>
                        
                    </div>
                    <div className="bg-gray-50 border-b border-gray-200 flex flex-row items-center shrink-0">
                        <div className="w-16 md:w-24 px-1 md:px-2 py-2 text-[10px] md:text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Dominio</div>
                        <div className="flex-1 px-1 md:px-2 py-2 text-[10px] md:text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Marca/Modelo</div>
                        <div className="w-20 md:w-24 px-1 md:px-2 py-2 text-[10px] md:text-xs font-bold text-gray-600 border-r border-gray-200 text-center truncate">Fecha Inscr.</div>
                        <div className="w-16 md:w-20 px-1 md:px-2 py-2 text-[10px] md:text-xs font-bold text-gray-600 text-center truncate">Estado</div>
                    </div>
                    <ul className="flex-1 overflow-y-auto no-scrollbar">
                        {loading ? (
                            <div className="p-4 text-gray-400 text-xs text-center italic">Cargando...</div>
                        ) : rodados && rodados.length > 0 ? (
                            rodados.map((item, idx) => (
                                <li 
                                    key={idx} 
                                    className={`px-2 py-3 cursor-pointer text-sm border-b border-gray-100 last:border-0 flex justify-between items-center transition-colors ${form.id_rodado === item.id_rodado ? 'bg-blue-50 text-blue-800' : 'text-gray-800 hover:bg-gray-50'}`}
                                    onClick={() => setSelectedRodado(item)}
                                >
                                    <div className="flex flex-row w-full items-center text-center">
                                        <div className="w-16 md:w-24 truncate px-1 md:px-2 border-r border-gray-100 text-[11px] md:text-sm font-medium">{item.dominio}</div>
                                        <div className="flex-1 truncate px-2 border-r border-gray-100 text-left text-[11px] md:text-sm">{item.marca} {item.modelo}</div>
                                        <div className="w-20 md:w-24 truncate px-1 md:px-2 border-r border-gray-100 text-[10px] md:text-xs">{item.f_incr || item.fecha_inscripcion ? (item.f_incr || item.fecha_inscripcion).substring(0,10).split('-').reverse().join('/') : '-'}</div>
                                        <div className="w-16 md:w-20 truncate px-1 md:px-2 flex items-center justify-center">
                                            <span className={`px-1 py-0.5 rounded-md text-[9px] md:text-[10px] font-medium border truncate max-w-full ${item.vigente ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                                {item.vigente ? 'Vigente' : 'Inactivo'}
                                            </span>
                                        </div>
                                    </div>
                                    {!esVisualizacion && (
                                        <button 
                                            type="button"
                                            title="Eliminar rodado"
                                            onClick={(e) => borrarRodado(item.id_rodado, e)}
                                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors ml-2"
                                        >
                                            <TrashIcon width={18} height={18} />
                                        </button>
                                    )}
                                </li>
                            ))
                        ) : (
                            <div className="p-4 text-gray-400 text-xs text-center flex flex-col items-center justify-center h-full italic">No hay rodados registrados</div>
                        )}
                    </ul>
                </div>

                {/* Right side: Form */}
                <div className="flex flex-col col-span-1 lg:col-span-7 bg-gray-50 border border-gray-200 rounded-md p-5 shadow-sm overflow-y-auto max-h-[500px]">
                    <Typography variant="small" color="blue-gray" className="mb-4 font-semibold uppercase tracking-wider text-xs border-b border-gray-200 pb-2">
                        {form.id_rodado ? 'Detalle del Rodado' : 'Nuevo Rodado'}
                    </Typography>

                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-700">Dominio</span>
                                <input type="text" name="dominio" disabled={esVisualizacion} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full" value={form.dominio || ''} onChange={handleFormChange} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-700">Marca</span>
                                <input type="text" name="marca" disabled={esVisualizacion} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full" value={form.marca || ''} onChange={handleFormChange} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-700">Modelo</span>
                                <input type="text" name="modelo" disabled={esVisualizacion} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full" value={form.modelo || ''} onChange={handleFormChange} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-700">Fecha Inscripción</span>
                                <input type="date" name="f_incr" disabled={esVisualizacion} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full" value={form.f_incr || ''} onChange={handleFormChange} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-700">Lugar Inscripción</span>
                                <input type="text" name="lugar_inscr" disabled={esVisualizacion} className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full" value={form.lugar_inscr || ''} onChange={handleFormChange} />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-3 border border-gray-200 rounded-md">
                            {[
                                {label: 'Vigente', name: 'vigente'},
                                {label: 'Vendido', name: 'vendido'},
                                {label: 'Transferido', name: 'transferido'},
                                {label: 'Título', name: 'titulo'},
                                {label: 'Exención', name: 'exencion'},
                                {label: 'Póliza', name: 'poliza'},
                                {label: 'Seguro', name: 'seguro'}
                            ].map((ch) => (
                                <label key={ch.name} className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" disabled={esVisualizacion} name={ch.name} checked={!!form[ch.name]} onChange={handleFormChange} className="w-4 h-4 text-blue-600 bg-white" />
                                    <span className="text-sm text-gray-700">{ch.label}</span>
                                </label>
                            ))}
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-700">Observaciones</span>
                            <textarea name="observaciones" disabled={esVisualizacion} value={form.observaciones || ''} onChange={handleFormChange} rows="3" className="text-sm p-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 w-full"></textarea>
                        </div>

                        {!esVisualizacion && (
                            <div className="flex justify-end mt-2 pt-3 border-t border-gray-200">
                                
                                <button
                                    type="button"
                                    onClick={abrirNuevo}
                                    className="text-red-400 bg-red-100 hover:bg-red-200 py-2 px-6 font-bold rounded-md shadow-sm transition-all text-sm uppercase mr-2"
                                    title="Limpiar selección / Nuevo"
                                >
                                    LIMPIAR FILTROS
                                </button>
                                <button type="button" onClick={guardarRodado} className="flex items-center bg-blue-500 hover:bg-blue-600 text-white py-2 px-5 rounded-md text-sm font-bold transition-colors shadow-sm"><CheckIcon className="w-5 h-5 mr-1" strokeWidth={2} /> GUARDAR</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
